import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getStoryById, getStories } from "../../api/apiHandler";
import { webStories as fallbackStories } from "../home/homeData";
import { getMediaBaseUrl } from "../../utils/backendUrl";

const WebStoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [webStories, setWebStories] = useState([]);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const BASE_URL = getMediaBaseUrl();

  const resolveImage = (filename) => {
    if (!filename) return "";
    if (filename.startsWith("http")) return filename;
    if (filename.startsWith("/images/")) return filename;
    return `${BASE_URL}/uploads/stories/${encodeURIComponent(filename)}`;
  };

  const mapStory = (item) => {
    const slides = [];
    if (item.image) {
      slides.push({ image: resolveImage(item.image), heading: item.heading || "" });
    }
    const extraImages = Array.isArray(item.images) ? item.images : [];
    const extraHeadings = Array.isArray(item.image_headings) ? item.image_headings : [];
    extraImages.forEach((img, i) => {
      slides.push({
        image: resolveImage(img),
        heading: extraHeadings[i] || "",
      });
    });
    return {
      id: item.id,
      category: item.category || "",
      slides: slides.length > 0 ? slides : [{ image: "", heading: item.heading || "" }],
    };
  };

  useEffect(() => {
    let isMounted = true;
    const loadStories = async () => {
      try {
        const [allRes, oneRes] = await Promise.all([
          getStories(),
          getStoryById(id).catch(() => null),
        ]);
        if (!isMounted) return;

        const allItems = Array.isArray(allRes?.data) ? allRes.data : [];
        const mappedAll = allItems.map(mapStory);
        const baseList = mappedAll.length > 0 ? mappedAll : fallbackStories;

        let targetIndex = baseList.findIndex(
          (s) => String(s.id) === String(id)
        );
        if (targetIndex === -1) {
          const oneData = oneRes?.data;
          if (oneData) {
            const mappedOne = mapStory(oneData);
            targetIndex = 0;
            baseList.unshift(mappedOne);
          } else {
            targetIndex = 0;
          }
        }

        setWebStories(baseList);
        setActiveStoryIndex(targetIndex);
        setCurrentSlide(0);
      } catch (err) {
        console.error("Error fetching web story:", err);
        if (isMounted) {
          const fallbackIndex = fallbackStories.findIndex(
            (s) => String(s.id) === String(id)
          );
          setWebStories(fallbackStories);
          setActiveStoryIndex(fallbackIndex >= 0 ? fallbackIndex : 0);
          setCurrentSlide(0);
        }
      }
    };
    loadStories();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activeStory = webStories[activeStoryIndex] || null;

  const goToStory = (index) => {
    if (index < 0 || index >= webStories.length) return;
    setActiveStoryIndex(index);
    setCurrentSlide(0);
    setIsPaused(false);
    navigate(`/web-stories/${webStories[index].id}`);
  };

  const nextSlide = () => {
    if (!activeStory) return;
    if (currentSlide < activeStory.slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else if (activeStoryIndex < webStories.length - 1) {
      goToStory(activeStoryIndex + 1);
    }
  };

  const prevSlide = () => {
    if (!activeStory) return;
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    } else if (activeStoryIndex > 0) {
      const previousStory = webStories[activeStoryIndex - 1];
      goToStory(activeStoryIndex - 1);
      setCurrentSlide(previousStory.slides.length - 1);
    }
  };

  const handleShare = async () => {
    if (!activeStory) return;
    if (navigator.share) {
      await navigator.share({
        title: activeStory.slides[currentSlide]?.heading || activeStory.heading,
        url: window.location.href,
      });
    } else {
      alert("इस ब्राउज़र में शेयर करना समर्थित नहीं है");
    }
  };

  useEffect(() => {
    if (!activeStory || isPaused) return;

    const timer = setTimeout(() => {
      if (currentSlide < activeStory.slides.length - 1) {
        setCurrentSlide((prev) => prev + 1);
      } else if (activeStoryIndex < webStories.length - 1) {
        setActiveStoryIndex((prevIndex) => prevIndex + 1);
        setCurrentSlide(0);
      } else {
        navigate("/");
      }
    }, 3000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, activeStoryIndex, isPaused, activeStory]);

  if (!activeStory) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-500 font-semibold">स्टोरी लोड हो रही है...</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-zinc-950 h-[100dvh] flex flex-col items-center justify-between overflow-hidden py-3 sm:py-4 px-3 sm:px-4 relative">
      {/* HEADER */}
      <div className="w-full max-w-2xl flex items-center justify-between shrink-0">
        <Link
          to="/"
          className="flex items-center gap-2 text-white bg-brand-red/40 backdrop-blur-md rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-bold hover:bg-brand-red transition"
        >
          <i className="fa-solid fa-arrow-left"></i> वेब स्टोरीजgchvjbjnm
        </Link>
        <span className="text-purple-300 text-xs sm:text-sm font-bold bg-zinc-900 border border-zinc-700 rounded-full px-3 sm:px-4 py-1.5">
          {activeStory.category}
        </span>
      </div>

      <div className="relative w-full flex-1 min-h-0 max-w-[420px] rounded-xl overflow-hidden bg-brand-red shadow-2xl ring-4 ring-zinc-800">
        {/* PROGRESS BARS */}
        <div className="absolute top-4 left-3 right-3 flex gap-2 z-20">
          {activeStory.slides.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-1.5 bg-gray-600/70 rounded-full overflow-hidden backdrop-blur-sm"
            >
              <div
                key={`${activeStoryIndex}-${index}-${index === currentSlide ? currentSlide : "done"}`}
                className={`h-full bg-purple-700 ${
                  index === currentSlide
                    ? "animate-progress"
                    : index < currentSlide
                    ? "w-full"
                    : "w-0"
                }`}
                style={{
                  animationDuration: "3000ms",
                  animationFillMode: "forwards",
                  animationPlayState: isPaused ? "paused" : "running",
                }}
              ></div>
            </div>
          ))}
        </div>

        {/* HEADER CONTROLS */}
        <div className="absolute top-10 left-4 right-4 flex justify-between items-center text-white z-20">
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-brand-red/40 backdrop-blur-md rounded-full text-xs sm:text-sm hover:text-brand-red transition"
          >
            {isPaused ? (
              <i className="fa-solid fa-play"></i>
            ) : (
              <i className="fa-solid fa-pause"></i>
            )}
          </button>

          <button
            onClick={handleShare}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-brand-red/40 backdrop-blur-md rounded-full text-xs sm:text-sm hover:text-brand-red transition"
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>
        </div>

        {/* LEFT ARROW */}
        <button
          onClick={prevSlide}
          aria-label="Previous"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center bg-black/50 backdrop-blur-md rounded-full text-white text-base hover:bg-brand-red transition border border-white/20 shadow-lg"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>

        {/* RIGHT ARROW */}
        <button
          onClick={nextSlide}
          aria-label="Next"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center bg-black/50 backdrop-blur-md rounded-full text-white text-base hover:bg-brand-red transition border border-white/20 shadow-lg"
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>

        {/* CURRENT IMAGE */}
        <img
          key={currentSlide}
          src={activeStory.slides[currentSlide].image}
          alt=""
          className="w-full h-full object-cover transition-opacity duration-300"
        />

        {/* HEADING/CAPTION */}
        <div className="absolute bottom-3 sm:bottom-5 left-3 right-3 z-20 text-white bg-brand-red/70 backdrop-blur-lg p-3 sm:p-4 rounded-xl shadow-xl border border-white/10 pointer-events-none">
          <h2
            key={currentSlide}
            className="text-sm sm:text-base text-center font-bold animate-slideFadeUp leading-snug drop-shadow-md"
          >
            {activeStory.slides[currentSlide].heading}
          </h2>
        </div>
      </div>

      {/* CLOSE / BACK TO HOME */}
      <div className="mt-2 flex items-center justify-end w-full max-w-2xl shrink-0">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-white bg-brand-red/40 backdrop-blur-md rounded-full px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-bold hover:bg-brand-red transition"
        >
          <i className="fa-solid fa-xmark"></i> बंद करें
        </button>
      </div>
    </div>
  );
};

export default WebStoryDetail;
